import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'

type ProjectLike = Pick<ProjectDocument, 'id' | 'projectKey' | 'parentProjectId' | 'isDeleted'>

/**
 * The space the current route sits in, so the sidebar can drill into it
 * (design 21 C). Covers the space's own pages, its projects under
 * /spaces/…/projects/…, and the flat /projects/$projectKey routes.
 */
export function resolveRouteSpaceId(
  pathname: string,
  projects: ReadonlyArray<ProjectLike>,
): string | null {
  const segments = pathname.split('/').filter(Boolean)
  const section = segments[1]
  const key = segments[2]?.toLowerCase()
  if (!key || (section !== 'spaces' && section !== 'projects')) return null

  const live = projects.filter((p) => !p.isDeleted)
  if (section === 'spaces') {
    return live.find((p) => !p.parentProjectId && p.projectKey.toLowerCase() === key)?.id ?? null
  }

  const project = live.find((p) => p.projectKey.toLowerCase() === key)
  if (!project) return null
  if (!project.parentProjectId) return project.id
  return live.some((p) => p.id === project.parentProjectId) ? project.parentProjectId : null
}

/**
 * Which space the drilled-in sidebar shows. A manual choice ("All spaces" or
 * clicking a space row) holds until the route moves to a different space;
 * then the route wins again.
 */
export function effectiveDrillSpaceId(
  routeSpaceId: string | null,
  manual: { spaceId: string | null; routeSpaceId: string | null } | null,
): string | null {
  if (manual && manual.routeSpaceId === routeSpaceId) return manual.spaceId
  return routeSpaceId
}

interface TaskLike {
  projectId?: string | null
  statusId: string
  parentId?: string | null
  isArchived?: boolean
  isDeleted?: boolean
}

/** Open, top-level, unarchived tasks per project id. */
export function countOpenTasksByProject(
  tasks: ReadonlyArray<TaskLike>,
  openStatusIds: ReadonlyArray<string>,
): Map<string, number> {
  const open = new Set(openStatusIds)
  const counts = new Map<string, number>()
  for (const task of tasks) {
    if (!task.projectId || task.isDeleted || task.isArchived || task.parentId) continue
    if (!open.has(task.statusId)) continue
    counts.set(task.projectId, (counts.get(task.projectId) ?? 0) + 1)
  }
  return counts
}

export interface ProjectDeletePlan {
  /** A workspace keeps at least one space, so its last one can't be deleted. */
  isLastSpace: boolean
  /** Open tasks (not done, cancelled or archived) block the delete. */
  openTaskCount: number
  /** Done, cancelled and archived tasks go with the project. */
  taskIds: Array<string>
  /** A space's projects. */
  projectIds: Array<string>
  pageIds: Array<string>
}

/**
 * What deleting a project or space takes with it: a space's projects, every
 * page and closed task in any of them, and whether open tasks stand in the way.
 */
export function planProjectDelete(
  projectId: string,
  projects: ReadonlyArray<Pick<ProjectDocument, 'id' | 'parentProjectId' | 'isDeleted'>>,
  pages: ReadonlyArray<{ id: string; projectId: string; isDeleted?: boolean }>,
  tasks: ReadonlyArray<{ id: string; projectId?: string | null; statusId: string; isArchived?: boolean; isDeleted?: boolean }>,
  closedStatusIds: ReadonlySet<string>,
): ProjectDeletePlan {
  const live = projects.filter((p) => !p.isDeleted)
  const isSpace = !live.find((p) => p.id === projectId)?.parentProjectId
  const projectIds = live.filter((p) => p.parentProjectId === projectId).map((p) => p.id)
  const inTree = new Set([projectId, ...projectIds])
  const treeTasks = tasks.filter((t) => !t.isDeleted && !!t.projectId && inTree.has(t.projectId))
  const isClosed = (t: (typeof treeTasks)[number]) => !!t.isArchived || closedStatusIds.has(t.statusId)
  return {
    isLastSpace: isSpace && live.filter((p) => !p.parentProjectId).length <= 1,
    openTaskCount: treeTasks.filter((t) => !isClosed(t)).length,
    taskIds: treeTasks.filter(isClosed).map((t) => t.id),
    projectIds,
    pageIds: pages.filter((p) => !p.isDeleted && inTree.has(p.projectId)).map((p) => p.id),
  }
}
